import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { getActiveStringing, updateStringingStatus, fetchAdminProfile } from "../../../api/api";
import { SearchContext } from "../../../context/SearchContext";
import Pagination from "../../../components/layout/Pagination/Pagination";
import styles from "./ActiveStringing.module.css";

const formatPickupOption = (option) => {
  if (!option) return "N/A";
  switch (option) {
    case "pickup":
      return "Pickup";
    case "delivery":
      return "Delivery";
    case "pickup/delivery":
      return "Pickup & Delivery";
    case "in-store":
      return "In-Store";
    case "express-pickup":
      return "Express Pickup";
    case "express-delivery":
      return "Express Delivery";
    case "express-pickup/delivery":
      return "Express Pickup & Delivery";
    default:
      return option
        .split(/[\s/-]+/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
  }
};

const formatTension = (job) => {
  if (job.service_kind !== "string") return null;
  const tt = job.tension_type;
  if (!tt) return null;
  if (tt === "support") return "Talk with support";
  if (job.is_hybrid === 1 || job.is_hybrid) {
    if (tt === "standard") return "Main: Standard | Cross: Standard";
    const unit = job.tension_unit || "lbs";
    const mainPrestretch = job.prestretch ? ` (Prestretch: ${job.prestretch_percent ?? "—"}%)` : "";
    const crossPrestretch = job.prestretch_cross ? ` (Prestretch: ${job.prestretch_percent_cross ?? "—"}%)` : "";
    const mainStr = job.tension_main != null ? `${job.tension_main} ${unit}${mainPrestretch}` : "Standard";
    const crossStr = job.tension_cross != null ? `${job.tension_cross} ${unit}${crossPrestretch}` : "Standard";
    return `Main: ${mainStr} | Cross: ${crossStr}`;
  }
  if (tt === "standard") return "Standard";
  if (tt === "custom") {
    const unit = job.tension_unit || "lbs";
    let label = `${job.tension_main ?? "—"} ${unit} main / ${job.tension_cross ?? "—"} ${unit} cross`;
    if (job.prestretch) label += `, Prestretch: ${job.prestretch_percent ?? "—"}%`;
    return label;
  }
  return "—";
};

const ActiveStringing = () => {
  const navigate = useNavigate();
  const { searchTerm } = useContext(SearchContext);
  const [activeJobs, setActiveJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [userRole, setUserRole] = useState("");
  const [userLocation, setUserLocation] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [updatingId, setUpdatingId] = useState(null);
  const [activeTab, setActiveTab] = useState("active");

  const itemsPerPage = 8;

  const fetchProfileAndJobs = async (tab = activeTab) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("adminToken");
      if (!token) {
        navigate("/login", {
          state: { error: "Session expired. Please log in again." },
        });
        return;
      }

      // Load Profile
      const role = localStorage.getItem("adminRole") || "";
      setUserRole(role);
      
      let location = "";
      try {
        const profileRes = await fetchAdminProfile(token);
        location = profileRes.data.location || "";
        setUserLocation(location);
      } catch (err) {
        console.warn("Could not fetch admin location profile:", err.message);
      }

      // Fetch Stringing Jobs based on tab selection
      const jobsRes = await getActiveStringing(tab === "completed");
      setActiveJobs(jobsRes.data);
      setLoading(false);
    } catch (err) {
      setError(err.message || "Failed to load jobs.");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfileAndJobs(activeTab);
    setCurrentPage(1);
  }, [activeTab]);

  const handleUpdateStatus = async (job, nextStatus) => {
    const bookingId = job.booking_id || job.grip_booking_id;
    const isGrip = job.service_kind === "grip";
    
    setUpdatingId(bookingId);
    try {
      await updateStringingStatus(bookingId, isGrip, nextStatus);
      
      // Update UI state locally
      if (nextStatus === "completed") {
        // Completed items disappear from the active jobs list
        setActiveJobs((prev) => prev.filter((j) => (j.booking_id || j.grip_booking_id) !== bookingId));
      } else {
        setActiveJobs((prev) =>
          prev.map((j) => {
            const id = j.booking_id || j.grip_booking_id;
            return id === bookingId ? { ...j, stringing_status: nextStatus } : j;
          })
        );
      }
    } catch (err) {
      console.error("Error updating status:", err);
      alert(err.response?.data?.message || err.message || "Failed to update status.");
    } finally {
      setUpdatingId(null);
    }
  };

  const filteredJobs = activeJobs.filter((job) => {
    const custName = (job.customer_name || "").toLowerCase();
    const serviceName = (job.service_name || "").toLowerCase();
    const racketName = (job.racket_name || "").toLowerCase();
    const search = searchTerm.toLowerCase();

    return (
      custName.includes(search) ||
      serviceName.includes(search) ||
      racketName.includes(search)
    );
  });

  const totalPages = Math.ceil(filteredJobs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const displayedJobs = filteredJobs.slice(startIndex, startIndex + itemsPerPage);

  if (currentPage > totalPages && totalPages > 0) {
    setCurrentPage(1);
  }

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Active Stringing & Gripping</h2>
      
      {userLocation && (
        <p className={styles.locationBanner}>
          Showing jobs for: <strong>{userLocation}</strong> ({userRole})
        </p>
      )}

      {/* Tabs Selection Container */}
      <div className={styles.tabContainer}>
        <button
          className={`${styles.tabButton} ${activeTab === "active" ? styles.activeTab : ""}`}
          onClick={() => setActiveTab("active")}
        >
          Active Services
        </button>
        <button
          className={`${styles.tabButton} ${activeTab === "completed" ? styles.activeTab : ""}`}
          onClick={() => setActiveTab("completed")}
        >
          Completed Rackets
        </button>
      </div>

      {loading && <p className={styles.message}>Loading stringing jobs...</p>}
      {error && <p className={styles.error}>{error}</p>}

      {!loading && !error && (
        <div className={styles.jobList}>
          {displayedJobs.length > 0 ? (
            displayedJobs.map((job) => {
              const bookingId = job.booking_id || job.grip_booking_id;
              const isGrip = job.service_kind === "grip";
              const currentStatus = job.stringing_status || "pending";

              return (
                <div key={`${bookingId}-${job.service_kind}`} className={styles.jobCard}>
                  <div className={styles.cardHeader}>
                    <div>
                      <h3>{job.customer_name}</h3>
                      <span className={styles.bookingId}>#{bookingId}</span>
                    </div>
                    <span
                      className={`${styles.badge} ${
                        currentStatus === "received"
                          ? styles.received
                          : currentStatus === "ready"
                          ? styles.ready
                          : currentStatus === "completed"
                          ? styles.completedBadge
                          : styles.pending
                      }`}
                    >
                      {currentStatus === "pending"
                        ? "Pending Dropoff"
                        : currentStatus === "received"
                        ? "Racket Received"
                        : currentStatus === "ready"
                        ? "Strung & Ready"
                        : currentStatus === "completed"
                        ? "Completed"
                        : currentStatus}
                    </span>
                  </div>

                  <div className={styles.cardBody}>
                    <p><strong>Racket:</strong> {job.racket_name || "N/A"}</p>
                    {job.service_kind === "string" && (job.is_hybrid === 1 || job.is_hybrid) ? (
                      <div className={styles.hybridContainer}>
                        <div className={styles.hybridRow}>
                          <span className={styles.hybridTypeBadge}>Main (Vertical)</span>
                          <span className={styles.hybridStringInfo}>
                            <strong>{job.string_name || "BYOS"}</strong>
                            {job.string_brand && <span className={styles.hybridBrand}> ({job.string_brand})</span>}
                          </span>
                          <span className={styles.hybridTensionInfo}>
                            Tension: <strong>{job.tension_main != null
                              ? `${job.tension_main} ${job.tension_unit || "lbs"}${job.prestretch ? ` (+${job.prestretch_percent ?? "—"}% Prestretch)` : ""}`
                              : (job.tension_type === "support" ? "Support" : "Standard")}</strong>
                          </span>
                        </div>
                        <div className={styles.hybridRow}>
                          <span className={styles.hybridTypeBadge}>Cross (Horizontal)</span>
                          <span className={styles.hybridStringInfo}>
                            <strong>{job.string_name_cross || "BYOS"}</strong>
                            {job.string_brand_cross && <span className={styles.hybridBrand}> ({job.string_brand_cross})</span>}
                          </span>
                          <span className={styles.hybridTensionInfo}>
                            Tension: <strong>{job.tension_cross != null
                              ? `${job.tension_cross} ${job.tension_unit || "lbs"}${job.prestretch_cross ? ` (+${job.prestretch_percent_cross ?? "—"}% Prestretch)` : ""}`
                              : (job.tension_type === "support" ? "Support" : "Standard")}</strong>
                          </span>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p>
                          <strong>Service:</strong> {job.service_name || "N/A"}{" "}
                          <span className={styles.serviceTag}>({job.service_kind})</span>
                        </p>
                        {job.service_kind === "string" && formatTension(job) && (
                          <p>
                            <strong>Tension:</strong>{" "}
                            <span style={{ color: "#0b7b43", fontWeight: "600" }}>
                              {formatTension(job)}
                            </span>
                          </p>
                        )}
                      </>
                    )}
                    <p><strong>Pickup/Delivery:</strong> {formatPickupOption(job.pickup_option)}</p>
                    <p><strong>Date Booked:</strong> {new Date(job.booking_date).toLocaleDateString("en-GB")}</p>
                    {job.customer_phone && <p><strong>Phone:</strong> {job.customer_phone}</p>}
                  </div>

                  {activeTab === "active" && (
                    <div className={styles.actionRow}>
                      {currentStatus === "pending" && (
                        <button
                          className={styles.receiveButton}
                          onClick={() => handleUpdateStatus(job, "received")}
                          disabled={updatingId !== null}
                        >
                          Mark as Received
                        </button>
                      )}
                      {currentStatus === "received" && (
                        <button
                          className={styles.readyButton}
                          onClick={() => handleUpdateStatus(job, "ready")}
                          disabled={updatingId !== null}
                        >
                          Mark as Ready
                        </button>
                      )}
                      {currentStatus === "ready" && (
                        <button
                          className={styles.completeButton}
                          onClick={() => handleUpdateStatus(job, "completed")}
                          disabled={updatingId !== null}
                        >
                          Mark as Completed
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <p className={styles.message}>No {activeTab} jobs found.</p>
          )}

          {filteredJobs.length > itemsPerPage && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => setCurrentPage(page)}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ActiveStringing;
